import { Form, Input, InputRef, Row, Tooltip } from "antd";
import { Link } from "react-router-dom";
import ROUTES from "../../configs/routesConfig";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { useCallback, useEffect, useMemo, KeyboardEvent } from "react";
import { FormInstance, Rule } from "antd/es/form";
import _ from "lodash";
import { useSearchCustomer } from "./hooks/useSearchCustomer";

type CustomerInputSearchProps = {
  form: FormInstance;
  name: string;
  idItemName: string;
  label: string;
  isFormDisabled: boolean;
  lastSearch?: boolean;
  placeholder?: string;
  additionalClass?: string;
  isFieldRequired?: boolean;
  customRef?: React.RefObject<InputRef>;
  onSuccess?: (value: any) => void;
};

export const CustomerInputSearch = ({
  form,
  name,
  idItemName,
  label,
  isFormDisabled,
  lastSearch,
  placeholder = "Input Customer#, “Enter” to confirm",
  additionalClass,
  customRef,
  isFieldRequired = true,
  onSuccess,
}: CustomerInputSearchProps) => {
  const setFieldError = useCallback(
    (errorMessages: string[]): void => {
      form.setFields([
        {
          name: name,
          errors: errorMessages,
        },
      ]);
    },
    [name, form],
  );

  const nameVal = Form.useWatch(name, form);

  const { customer, setCustomer, isDisabled, search } =
    useSearchCustomer(setFieldError);

  useEffect(() => {
    !nameVal && setCustomer(null);
  }, [setCustomer, nameVal]);

  useEffect(() => {
    form.setFieldValue(idItemName, customer ? customer.id : null);
  }, [customer, form, idItemName]);

  const rules = useMemo(() => {
    return [
      {
        required: isFieldRequired,
      },
      () => ({
        validateTrigger: "onSubmit",
        validator(_: Rule, value: string) {
          const idVal = form.getFieldValue(idItemName);

          if (!value || idVal) {
            return Promise.resolve("");
          }
          if (value && !idVal) {
            return Promise.reject(
              new Error("Please press Enter to find the user"),
            );
          }
          return Promise.reject(new Error("Customer was not found"));
        },
      }),
    ];
  }, [isFieldRequired, form, idItemName]);

  const onEnterClicked = useCallback(
    (e: KeyboardEvent<HTMLInputElement>): void => {
      const value = +e.currentTarget.value;

      if (value && !_.isNaN(value)) {
        search(value, onSuccess);
      } else {
        setCustomer(null);
        setFieldError(["Please enter correct number"]);
      }
    },
    [search, setCustomer, setFieldError, onSuccess],
  );

  useEffect(() => {
    const value = form.getFieldValue(name);

    if (value) {
      search(+value, onSuccess);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onInputChange = useCallback(() => setCustomer(null), [setCustomer]);

  const className: string = useMemo(
    () => `
            position-relative
            ${additionalClass || ""}
            ${customer && !lastSearch ? "pb-4" : ""}
          `,
    [additionalClass, customer, lastSearch],
  );

  return (
    <div className={className}>
      <Form.Item label={label} name={name} rules={rules}>
        <Input
          placeholder={placeholder}
          disabled={isFormDisabled || isDisabled}
          onPressEnter={onEnterClicked}
          onChange={onInputChange}
          ref={customRef}
        />
      </Form.Item>
      <Form.Item name={idItemName} hidden>
        <Input />
      </Form.Item>
      {customer && (
        <Row className="position-absolute customer-name-link elipsis align-center">
          <Link
            to={`${ROUTES.CUSTOMERS}/${customer.id}`}
            target="_blank"
            className="mr-2"
          >
            <span className="text-md mt-2 mb-0 ant-btn-link">
              {customer.name}
            </span>
          </Link>
          {!customer.hasTaxId && (
            <Tooltip
              placement="top"
              title="Tax ID is not specified"
              overlayClassName="text-sm"
            >
              <ExclamationCircleOutlined className="warning logo-cursor" />
            </Tooltip>
          )}
        </Row>
      )}
    </div>
  );
};
